import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  InteractionRequiredAuthError,
  PublicClientApplication,
} from "@azure/msal-browser";

import { useNavigate } from "react-router-dom";

import { sharepointCredentials } from "../utils/constants";
import { useAppState } from "./AppStateContext";
import api from "../utils/api";

/*
 * Change this to your actual backend login endpoint.
 *
 * For example:
 * const LOGIN_API_PATH = "/api/login";
 */
const LOGIN_API_PATH = "/api/protected";

const DEFAULT_USER_DATA = {
  userInitials: "",
  isSuperUser: false,
};

const validateScopes = configuredScopes => {
  if (!Array.isArray(configuredScopes)) {
    throw new TypeError(
      "sharepointCredentials.scopes must be an array."
    );
  }

  const normalizedScopes = configuredScopes.map(
    (scope, index) => {
      if (typeof scope !== "string") {
        throw new TypeError(
          `MSAL scope at index ${index} must be a string.`
        );
      }

      const normalizedScope = scope.trim();

      if (!normalizedScope) {
        throw new TypeError(
          `MSAL scope at index ${index} cannot be empty.`
        );
      }

      return normalizedScope;
    }
  );

  if (normalizedScopes.length === 0) {
    throw new Error("No valid MSAL scopes are configured.");
  }

  return normalizedScopes;
};

const validScopes = validateScopes(
  sharepointCredentials.scopes
);

// MSAL configuration
const msalConfig = {
  auth: {
    clientId: sharepointCredentials.clientId,
    authority: sharepointCredentials.authority,
    redirectUri: sharepointCredentials.redirectUri,
    postLogoutRedirectUri: `${window.location.origin}/logout`,
    navigateToLoginRequestUrl: false,
  },

  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
};

export const loginRequest = {
  scopes: validScopes,
};

export const apiTokenRequest = {
  scopes: validScopes,
};

export const msalInstance =
  new PublicClientApplication(msalConfig);

// Context
const SSOContext = createContext(null);

// Helpers
const getCurrentAccount = () => {
  return (
    msalInstance.getActiveAccount() ||
    msalInstance.getAllAccounts()[0] ||
    null
  );
};

const createLoggedInUserData = currentAccount => {
  const claims = currentAccount?.idTokenClaims;

  const accountNameParts =
    currentAccount?.name?.trim()?.split(/\s+/) ?? [];

  const givenName =
    claims?.given_name ||
    accountNameParts[0] ||
    "";

  const familyName =
    claims?.family_name ||
    accountNameParts.at(-1) ||
    "";

  const initials = `${givenName?.[0] ?? ""
    }${familyName?.[0] ?? ""}`.toUpperCase();

  return (
    initials ||
    currentAccount?.username?.[0]?.toUpperCase() ||
    ""
  );
};

const getErrorMessage = error => {
  if (!error) {
    return "An unknown authentication error occurred.";
  }

  if (typeof error === "string") {
    return error;
  }

  if (typeof error.response?.data === "string") {
    return error.response.data;
  }

  return (
    error.errorMessage ||
    error.message ||
    error.response?.data?.message ||
    error.response?.data?.detail ||
    error.response?.data?.error_description ||
    "Authentication failed."
  );
};

const getIsSuperUser = responseData => {
  /*
   * This handles the following response formats:
   *
   * { is_superuser: true }
   *
   * { data: { is_superuser: true } }
   *
   * { user: { is_superuser: true } }
   */

  const value =
    responseData?.is_superuser ??
    responseData?.data?.is_superuser ??
    responseData?.user?.is_superuser ??
    false;

  return Boolean(value);
};

// Provider
export const SSOProvider = ({ children }) => {
  const navigate = useNavigate();
  const { setApiRes } = useAppState();

  const [account, setAccount] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [userData, setUserData] = useState(
    DEFAULT_USER_DATA
  );

  /*
   * msalReady:
   * PublicClientApplication.initialize() completed.
   *
   * apiReady:
   * Axios interceptors have been registered.
   *
   * authReady:
   * The account and backend user information have been
   * loaded. PrivateRoute may now decide whether to render.
   */
  const [msalReady, setMsalReady] = useState(false);
  const [apiReady, setApiReady] = useState(false);
  const [authReady, setAuthReady] = useState(false);

  const [isLoggingIn, setIsLoggingIn] =
    useState(false);

  const [
    interactionRequired,
    setInteractionRequired,
  ] = useState(false);

  // Reuse token acquisition when multiple API requests
  // start simultaneously.
  const tokenPromiseRef = useRef(null);

  // Avoid loading the backend user more than once
  // for the same account.
  const userPromiseRef = useRef(null);
  const loadedUserAccountRef = useRef(null);

  // Avoid updating state after unmount.
  const mountedRef = useRef(true);

  // Avoid starting multiple login popup operations.
  const loginInProgressRef = useRef(false);

  // Error handling
  const reportAuthError = useCallback(
    (heading, error) => {
      console.error(heading, error);

      setApiRes(previous => ({
        ...previous,
        axiosError: true,
        errHeading: heading,
        errMsg: getErrorMessage(error),
      }));
    },
    [setApiRes]
  );

  // Account state
  const updateAccountState = useCallback(
    currentAccount => {
      if (!mountedRef.current) {
        return;
      }

      setAccount(currentAccount || null);

      setUserData(previous => ({
        ...previous,
        userInitials: currentAccount
          ? createLoggedInUserData(currentAccount)
          : "",
      }));
    },
    []
  );

  const clearAccountState = useCallback(() => {
    if (!mountedRef.current) {
      return;
    }

    tokenPromiseRef.current = null;
    userPromiseRef.current = null;
    loadedUserAccountRef.current = null;

    setAccount(null);
    setAccessToken(null);
    setUserData(DEFAULT_USER_DATA);
    setInteractionRequired(false);
    setAuthReady(true);
  }, []);

  // Initialize MSAL
  useEffect(() => {
    mountedRef.current = true;

    const initializeAuthentication = async () => {
      try {
        await msalInstance.initialize();

        const currentAccount = getCurrentAccount();

        if (currentAccount) {
          msalInstance.setActiveAccount(currentAccount);
          updateAccountState(currentAccount);
        }

        if (mountedRef.current) {
          setMsalReady(true);
        }
      } catch (error) {
        reportAuthError(
          "MSAL Initialization Error",
          error
        );

        /*
         * Allow the route to reach the login page instead
         * of displaying the loader permanently.
         */
        if (mountedRef.current) {
          setMsalReady(true);
          setApiReady(true);
          setAuthReady(true);
        }
      }
    };

    initializeAuthentication();

    return () => {
      mountedRef.current = false;
    };
  }, [reportAuthError, updateAccountState]);

  // Silent token acquisition
  const acquireTokenSilently = useCallback(
    async forceRefresh => {
      if (!msalReady) {
        throw new Error("MSAL_NOT_INITIALIZED");
      }

      /*
       * Multiple dashboard requests may start together.
       * They should share the same MSAL acquisition
       * promise.
       */
      if (tokenPromiseRef.current) {
        return tokenPromiseRef.current;
      }

      const tokenOperation = async () => {
        const currentAccount = getCurrentAccount();

        if (!currentAccount) {
          if (mountedRef.current) {
            setInteractionRequired(false);
          }

          throw new Error("AUTH_REQUIRED");
        }

        msalInstance.setActiveAccount(currentAccount);
        updateAccountState(currentAccount);

        try {
          const tokenResponse =
            await msalInstance.acquireTokenSilent({
              ...apiTokenRequest,
              account: currentAccount,
              forceRefresh: Boolean(forceRefresh),
            });

          const token = tokenResponse?.accessToken;

          if (!token) {
            throw new Error(
              "MSAL returned an empty API access token."
            );
          }

          if (mountedRef.current) {
            setAccessToken(token);
            setInteractionRequired(false);
          }

          return token;
        } catch (error) {
          if (
            error instanceof
            InteractionRequiredAuthError
          ) {
            if (mountedRef.current) {
              setInteractionRequired(true);
            }

            const interactionError = new Error(
              "INTERACTION_REQUIRED"
            );

            interactionError.cause = error;

            throw interactionError;
          }

          throw error;
        }
      };

      tokenPromiseRef.current = tokenOperation();

      try {
        return await tokenPromiseRef.current;
      } finally {
        tokenPromiseRef.current = null;
      }
    },
    [msalReady, updateAccountState]
  );

  /*
   * Calls the backend login API and copies is_superuser
   * into userData.isSuperUser.
   */
  const loadLoggedInUser = useCallback(
    async (currentAccount, token) => {
      if (!currentAccount) {
        throw new Error(
          "Cannot load the backend user without an account."
        );
      }

      if (!token) {
        throw new Error(
          "Cannot load the backend user without an access token."
        );
      }

      const accountKey =
        currentAccount.homeAccountId ||
        currentAccount.username;

      /*
       * If this account has already been loaded, do not
       * call the login API again.
       */
      if (
        loadedUserAccountRef.current === accountKey
      ) {
        return null;
      }

      /*
       * If another component is already loading the
       * backend user, reuse the same Promise.
       */
      if (userPromiseRef.current) {
        return userPromiseRef.current;
      }

      const userOperation = async () => {
        /*
         * The interceptor is skipped because the token
         * is being supplied explicitly here.
         *
         * If your login API needs a request body, replace
         * the empty object with the required information.
         */
        const response = await api.post(
          LOGIN_API_PATH,
          {},
          {
            skipAuth: true,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const isSuperUser = getIsSuperUser(
          response.data
        );

        if (mountedRef.current) {
          setUserData({
            userInitials:
              createLoggedInUserData(currentAccount),
            isSuperUser,
          });

          loadedUserAccountRef.current =
            accountKey;
        }

        return response.data;
      };

      userPromiseRef.current = userOperation();

      try {
        return await userPromiseRef.current;
      } catch (error) {
        loadedUserAccountRef.current = null;

        reportAuthError(
          "Login API Error",
          error
        );

        throw error;
      } finally {
        userPromiseRef.current = null;
      }
    },
    [reportAuthError]
  );

  // Install Axios interceptors
  useEffect(() => {
    if (!msalReady) {
      return undefined;
    }

    setApiReady(false);

    const requestInterceptor =
      api.interceptors.request.use(
        async config => {
          if (config.skipAuth === true) {
            return config;
          }

          try {
            const token =
              await acquireTokenSilently();

            config.headers = config.headers ?? {};

            config.headers.Authorization =
              `Bearer ${token}`;

            return config;
          } catch (error) {
            /*
             * Do not send an authenticated request
             * without a token.
             */
            console.error(
              "Authenticated request was blocked",
              {
                url: config.url,
                method: config.method,
                reason: error?.message,
              }
            );

            return Promise.reject(error);
          }
        },
        error => Promise.reject(error)
      );

    const responseInterceptor =
      api.interceptors.response.use(
        response => response,

        error => {
          if (error.response?.status === 401) {
            console.error(
              "Protected API returned 401",
              {
                method: error.config?.method,
                baseURL: error.config?.baseURL,
                url: error.config?.url,
                response: error.response?.data,
                hasAuthorizationHeader: Boolean(
                  error.config?.headers
                    ?.Authorization
                ),
                wwwAuthenticate:
                  error.response?.headers?.[
                  "www-authenticate"
                  ],
              }
            );
          }

          // Do not automatically call logout here.
          return Promise.reject(error);
        }
      );

    /*
     * The interceptors now exist. The user hydration
     * effect can safely call the backend.
     */
    if (mountedRef.current) {
      setApiReady(true);
    }

    return () => {
      api.interceptors.request.eject(
        requestInterceptor
      );

      api.interceptors.response.eject(
        responseInterceptor
      );
    };
  }, [msalReady, acquireTokenSilently]);

  /*
   * Restore backend user information after a browser
   * refresh.
   *
   * MSAL restores the account from localStorage, but
   * React state is lost on refresh. This effect calls
   * the backend login API again and restores isSuperUser.
   */
  useEffect(() => {
    if (!msalReady || !apiReady) {
      return undefined;
    }

    const currentAccount = getCurrentAccount();

    if (!currentAccount) {
      if (mountedRef.current) {
        setAuthReady(true);
      }

      return undefined;
    }

    let cancelled = false;

    const hydrateAuthentication = async () => {
      if (mountedRef.current) {
        setAuthReady(false);
      }

      try {
        const token =
          await acquireTokenSilently();

        if (cancelled) {
          return;
        }

        await loadLoggedInUser(
          currentAccount,
          token
        );
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (
          error?.message ===
          "INTERACTION_REQUIRED" ||
          error?.message === "AUTH_REQUIRED"
        ) {
          console.warn(
            "User interaction is required to restore authentication."
          );
        } else {
          console.error(
            "Could not restore backend user data.",
            error
          );
        }
      } finally {
        if (!cancelled && mountedRef.current) {
          setAuthReady(true);
        }
      }
    };

    hydrateAuthentication();

    return () => {
      cancelled = true;
    };
  }, [
    msalReady,
    apiReady,
    acquireTokenSilently,
    loadLoggedInUser,
  ]);

  // Login
  const login = useCallback(
    async (requestedReturnPath = "/") => {
      sessionStorage.clear();
      if (loginInProgressRef.current) {
        console.warn(
          "Login is already in progress."
        );

        return;
      }

      const returnPath =
        typeof requestedReturnPath === "string" &&
          requestedReturnPath.startsWith("/")
          ? requestedReturnPath
          : "/";

      loginInProgressRef.current = true;

      if (mountedRef.current) {
        setIsLoggingIn(true);
        setAuthReady(false);
      }

      try {
        let currentAccount =
          msalInstance.getActiveAccount() ||
          msalInstance.getAllAccounts()[0] ||
          null;

        /*
         * Only show the login popup when there is no
         * cached account.
         */
        if (!currentAccount) {
          const loginResponse =
            await msalInstance.loginPopup(
              loginRequest
            );

          if (!loginResponse?.account) {
            throw new Error(
              "Microsoft login did not return an account."
            );
          }

          currentAccount =
            loginResponse.account;
        }

        msalInstance.setActiveAccount(
          currentAccount
        );

        /*
         * This updates the account and initials
         * immediately.
         */
        updateAccountState(currentAccount);

        let tokenResponse;

        try {
          tokenResponse =
            await msalInstance.acquireTokenSilent({
              ...apiTokenRequest,
              account: currentAccount,
            });
        } catch (error) {
          if (
            error instanceof
            InteractionRequiredAuthError
          ) {
            tokenResponse =
              await msalInstance.acquireTokenPopup({
                ...apiTokenRequest,
                account: currentAccount,
              });
          } else {
            throw error;
          }
        }

        const token =
          tokenResponse?.accessToken;

        if (!token) {
          throw new Error(
            "Microsoft authentication did not return an access token."
          );
        }

        if (mountedRef.current) {
          setAccessToken(token);
          setInteractionRequired(false);
        }

        /*
         * Wait until is_superuser is copied into
         * userData before navigating.
         */
        await loadLoggedInUser(
          currentAccount,
          token
        );

        if (mountedRef.current) {
          setAuthReady(true);
        }

        navigate(returnPath, {
          replace: true,
        });
      } catch (error) {
        if (
          error?.errorCode ===
          "interaction_in_progress"
        ) {
          console.warn(
            "Another Microsoft authentication operation is still active."
          );

          return;
        }

        if (
          error instanceof
          InteractionRequiredAuthError
        ) {
          if (mountedRef.current) {
            setInteractionRequired(true);
          }
        }

        reportAuthError(
          "SSO Login Error",
          error
        );

        throw error;
      } finally {
        loginInProgressRef.current = false;

        if (mountedRef.current) {
          setIsLoggingIn(false);
          setAuthReady(true);
        }
      }
    },
    [
      loadLoggedInUser,
      navigate,
      reportAuthError,
      updateAccountState,
    ]
  );

  // Interactive token acquisition
  const acquireTokenInteractively =
    useCallback(async () => {
      if (!msalReady) {
        throw new Error(
          "MSAL has not completed initialization."
        );
      }

      const currentAccount =
        getCurrentAccount();

      if (!currentAccount) {
        await login("/");
        return null;
      }

      try {
        const tokenResponse =
          await msalInstance.acquireTokenPopup({
            ...apiTokenRequest,
            account: currentAccount,
          });

        const token =
          tokenResponse?.accessToken;

        if (!token) {
          throw new Error(
            "Interactive authentication did not return an access token."
          );
        }

        const responseAccount =
          tokenResponse.account ||
          currentAccount;

        msalInstance.setActiveAccount(
          responseAccount
        );

        updateAccountState(responseAccount);

        if (mountedRef.current) {
          setAccessToken(token);
          setInteractionRequired(false);
          setAuthReady(false);
        }

        /*
         * Refresh backend user information after
         * interactive token acquisition.
         */
        loadedUserAccountRef.current = null;

        await loadLoggedInUser(
          responseAccount,
          token
        );

        if (mountedRef.current) {
          setAuthReady(true);
        }

        return token;
      } catch (error) {
        reportAuthError(
          "Token Acquisition Error",
          error
        );

        if (mountedRef.current) {
          setAuthReady(true);
        }

        throw error;
      }
    }, [
      login,
      loadLoggedInUser,
      msalReady,
      reportAuthError,
      updateAccountState,
    ]);

  // Logout
  const logout = useCallback(async () => {
    const currentAccount =
      msalInstance.getActiveAccount() ||
      msalInstance.getAllAccounts()[0] ||
      null;

    clearAccountState();

    /*
     * Do not call localStorage.clear() here.
     *
     * MSAL uses localStorage for its authentication
     * cache. logoutRedirect will clean up the applicable
     * MSAL account information.
     *
     * Remove application-specific values individually:
     *
     * localStorage.removeItem("yourApplicationKey");
     */
    sessionStorage.clear();

    await msalInstance.logoutRedirect({
      account: currentAccount || undefined,
      postLogoutRedirectUri:
        `${window.location.origin}/logout`,
    });
  }, [clearAccountState]);

  // Context value
  const contextValue = useMemo(
    () => ({
      account,
      accessToken,
      userData,

      msalReady,
      apiReady,
      authReady,
      isLoggingIn,
      interactionRequired,

      isAuthenticated: Boolean(account),

      login,
      logout,

      acquireTokenSilently,
      acquireApiToken:
        acquireTokenSilently,

      acquireTokenInteractively,
      acquireApiTokenInteractively:
        acquireTokenInteractively,
    }),
    [
      account,
      accessToken,
      userData,
      msalReady,
      apiReady,
      authReady,
      isLoggingIn,
      interactionRequired,
      login,
      logout,
      acquireTokenSilently,
      acquireTokenInteractively,
    ]
  );

  return (
    <SSOContext.Provider value={contextValue}>
      {children}
    </SSOContext.Provider>
  );
};

export const useSSO = () => {
  const context = useContext(SSOContext);

  if (!context) {
    throw new Error(
      "useSSO must be used inside SSOProvider."
    );
  }

  return context;
};

export default SSOContext;